import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft, KeyRound } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/context/AuthContext";

export default function JoinCompany() {
  const nav = useNavigate();
  const { lang } = useLang();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ invite_code: "", name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 6) {
      setErr(lang === "en" ? "Password must be at least 6 characters" : "La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (form.invite_code.trim().length < 4) {
      setErr(lang === "en" ? "Enter the 6-char company code" : "Introduce el código de 6 caracteres de tu empresa");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        invite_code: form.invite_code.trim().toUpperCase(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        lang,
      };
      const { data } = await api.post("/auth/register-employee", payload);
      localStorage.setItem("sns_token", data.token);
      localStorage.setItem("sns_user", JSON.stringify(data.user));
      if (setUser) setUser(data.user);
      import("@/lib/push").then((m) => m.registerPushIfPossible().catch(() => {}));
      nav("/");
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md sns-fade-up">
        <button onClick={() => nav("/login")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6" data-testid="btn-back-login">
          <ArrowLeft size={16} /> {lang === "en" ? "Back to login" : "Volver al login"}
        </button>

        <div className="flex justify-center mb-6">
          <img src="/images/logo-brand.png" alt="Smile & Shine" className="h-24 rounded-3xl shadow-lg shadow-slate-300/50" />
        </div>

        <h1 className="sns-title text-3xl mb-2">
          {lang === "en" ? "Join your company" : "Únete a tu empresa"}
        </h1>
        <p className="text-slate-500 mb-6">
          {lang === "en"
            ? "Enter the 6-char code your manager shared with you."
            : "Introduce el código de 6 caracteres que te dio tu gerente."}
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="join-form">
          <div>
            <label className="block text-sm font-semibold mb-2">
              {lang === "en" ? "Company code" : "Código de empresa"}
            </label>
            <div className="relative">
              <KeyRound size={18} className="absolute inset-y-0 left-0 my-auto ml-4 text-slate-400" />
              <input
                type="text"
                placeholder="ABC123"
                className="sns-input pl-12 uppercase tracking-[0.3em] font-mono font-bold text-center text-lg"
                value={form.invite_code}
                onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })}
                maxLength={10}
                required
                data-testid="input-invite-code"
                autoComplete="off"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              {lang === "en" ? "Your name" : "Tu nombre"}
            </label>
            <input
              type="text"
              className="sns-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="input-name"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Email</label>
            <input
              type="email"
              className="sns-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value.trim().toLowerCase() })}
              required
              data-testid="input-email"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              {lang === "en" ? "Password (min 6 chars)" : "Contraseña (mín. 6 caracteres)"}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="sns-input pr-12"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                data-testid="input-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="join-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="sns-btn sns-btn-accent w-full text-base py-4 disabled:opacity-60"
            data-testid="btn-join-company"
          >
            {loading
              ? lang === "en" ? "Joining..." : "Uniéndote..."
              : lang === "en" ? "Join company" : "Unirme a la empresa"}
          </button>

          <div className="text-center pt-4 border-t mt-2">
            <p className="text-sm text-slate-500">
              {lang === "en" ? "Are you a company owner?" : "¿Eres gerente de una empresa?"}{" "}
              <button type="button" onClick={() => nav("/register")} className="text-[var(--coral)] font-semibold hover:underline" data-testid="link-to-register">
                {lang === "en" ? "Create your company" : "Crea tu empresa"}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
